<script setup lang="ts">
/**
 * Business-services breakdown cell for the Stuff table: a dated line-item
 * list of every business service the performer delivered in the period,
 * with the price earned per service — mirrors the sketch's `.lessons-table`
 * (`docs/sketches/desktop/performers-stat-table.html`). The period summary
 * (business services count, total income, unique clients) lives in
 * `PerformerNameCell` instead, per feedback.
 *
 * All values arrive pre-formatted/pre-sorted from `StuffTab` — this
 * component only lays them out, matching `FinanceTotalsRowCell`'s "dumb
 * renderer" role.
 *
 * The gender balance column matches `BusinessServiceCard`'s established
 * gender-badge convention (`NM / NF` in `--color-gender-men`/
 * `--color-gender-women`) rather than inventing a new visual style.
 */
import { useI18n } from 'vue-i18n'

const { t } = useI18n()

defineProps<{
  /** One row per business service, sorted by date, with pre-formatted date/price. */
  businessServices: {
    dateFormatted: string
    serviceName: string
    priceFormatted: string
    /** Count of distinct male clients who attended this business service. */
    maleClientsCnt: number
    /** Count of distinct female clients who attended this business service. */
    femaleClientsCnt: number
    /** Group the service was held for; '' when none. */
    groupName: string
    /** Group age at the end of the period; '' when there is no group. */
    groupAge: string
  }[]
  /** Max width of the cell (CSS value). Set by DynamicTable from column config. */
  maxWidth?: string
}>()
</script>

<template>
  <td
    class="performer-stat-cell font-tufte"
    :style="maxWidth ? { maxWidth: maxWidth, overflowWrap: 'break-word' } : undefined"
  >
    <table v-if="businessServices.length" class="performer-stat-cell__lessons">
      <thead>
        <tr>
          <th>{{ t('common.date') }}</th>
          <th>{{ t('common.service') }}</th>
          <th>{{ t('common.group') }}</th>
          <th>{{ t('common.age') }}</th>
          <th class="performer-stat-cell__col-r">{{ t('common.earned') }}</th>
          <th class="performer-stat-cell__col-r">{{ t('tabs.stuff.colGenderBalance') }}</th>
        </tr>
      </thead>
      <tbody>
        <tr v-for="(bs, i) in businessServices" :key="i">
          <td>{{ bs.dateFormatted }}</td>
          <td class="performer-stat-cell__svc">{{ bs.serviceName }}</td>
          <td class="performer-stat-cell__group">
            <template v-if="bs.groupName">{{ bs.groupName }}</template>
            <span v-else class="performer-stat-cell__none">–</span>
          </td>
          <td class="performer-stat-cell__age">{{ bs.groupAge }}</td>
          <td class="performer-stat-cell__col-r performer-stat-cell__price">
            {{ bs.priceFormatted }}
          </td>
          <td class="performer-stat-cell__col-r">
            <span class="performer-stat-cell__gender-badge">
              <span class="performer-stat-cell__gender-men"
                >{{ bs.maleClientsCnt }}{{ t('common.maleAbbr') }}</span
              >
              <span class="performer-stat-cell__gender-separator">/</span>
              <span class="performer-stat-cell__gender-women"
                >{{ bs.femaleClientsCnt }}{{ t('common.femaleAbbr') }}</span
              >
            </span>
          </td>
        </tr>
      </tbody>
    </table>
    <div v-else class="performer-stat-cell__empty">{{ t('tabs.stuff.emptyLessons') }}</div>
  </td>
</template>

<style scoped>
.performer-stat-cell {
  vertical-align: top;
}

.performer-stat-cell__lessons {
  width: 100%;
  border-collapse: collapse;
}

.performer-stat-cell__lessons th {
  font-family: var(--font-tufte-sans);
  font-size: 0.7rem;
  letter-spacing: 0.02em;
  text-transform: uppercase;
  color: var(--color-tufte-muted);
  text-align: left;
  white-space: nowrap;
  padding: 0.15rem 1.5rem 0.2rem 0;
  border-bottom: 1px solid var(--color-tufte-border);
  font-weight: 600;
}

.performer-stat-cell__lessons td {
  font-family: var(--font-tufte-mono);
  font-size: 0.75rem;
  white-space: nowrap;
  padding: 0.2rem 1.5rem 0.2rem 0;
  border-bottom: 1px solid color-mix(in srgb, var(--color-tufte-border) 50%, transparent);
}

.performer-stat-cell__lessons th:last-child,
.performer-stat-cell__lessons td:last-child {
  padding-right: 0;
}

.performer-stat-cell__svc {
  font-family: var(--font-tufte-sans);
}

.performer-stat-cell__none {
  color: var(--color-tufte-muted);
}

.performer-stat-cell__age {
  color: var(--color-tufte-muted);
  white-space: nowrap;
}

/*
 * Scoped under `.performer-stat-cell__lessons` (two classes) so it outranks
 * the `.performer-stat-cell__lessons th` rule above (one class + one type)
 * for header cells — otherwise that rule's own `text-align: left` won on
 * specificity and the Earned/Balance headers stayed left-aligned while
 * their values were right-aligned, off by the same x each row.
 */
.performer-stat-cell__lessons .performer-stat-cell__col-r {
  text-align: right;
}

/* Same money-positive color as MoneyCell / PerformerNameCell's income value — every earned amount here is a positive figure. */
.performer-stat-cell__price {
  color: var(--color-money-positive);
  font-variant-numeric: tabular-nums;
}

/* Same gender badge as BusinessServiceCard's visitor count (`NM / NF`, --color-gender-*). */
.performer-stat-cell__gender-badge {
  font-family: var(--font-tufte-mono);
  font-weight: 600;
  white-space: nowrap;
  font-variant-numeric: tabular-nums;
}

.performer-stat-cell__gender-men {
  color: var(--color-gender-men);
}

.performer-stat-cell__gender-women {
  color: var(--color-gender-women);
}

.performer-stat-cell__gender-separator {
  color: var(--color-tufte-muted);
  margin: 0 0.15rem;
}

.performer-stat-cell__empty {
  font-family: var(--font-tufte-sans);
  font-size: var(--text-tufte-sm);
  color: var(--color-tufte-muted);
  font-style: italic;
}
</style>
